import { api } from "./api";

/**
 * PrintQ Automated Background Print Service
 * Publisher: JSM DIGITAL SOLUTIONS
 *
 * Responsibilities:
 * 1. Monitors Print Jobs Queue in real-time.
 * 2. Matches Print Type (B&W vs Color) with configured Shop Machines.
 * 3. Automatic Silent Direct Printing in Electron Desktop environment.
 * 4. Offline printer queueing & status updates.
 * 5. Native PDF preservation & Vector printing without rasterization.
 */

class AutoPrintService {
  constructor() {
    this.shop = null;
    this.isElectron = Boolean(window.jsmDesktop?.printers?.printDocument);
    this.isPolling = false;
    this.pollInterval = null;
    this.activePrintingJobs = new Set();
    this.listeners = new Set();
    this.detectedPrinters = [];
  }

  // Subscribe to auto-print notifications
  subscribe(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  notify(event) {
    this.listeners.forEach((cb) => {
      try {
        cb(event);
      } catch (e) {
        console.error("AutoPrint listener error:", e);
      }
    });
  }

  setShop(shop) {
    this.shop = shop;
  }

  setDetectedPrinters(printers) {
    this.detectedPrinters = Array.isArray(printers) ? printers : [];
  }

  start(shop, onNewJob) {
    if (shop) {
      this.startAutoWatcher(shop, onNewJob);
    }
  }

  stop() {
    this.stopAutoWatcher();
  }

  isAutoPrintEnabled() {
    return Boolean(this.shop?.auto_print_enabled);
  }

  setAutoPrintEnabled(enabled) {
    if (this.shop) {
      this.shop.auto_print_enabled = Boolean(enabled);
    }
  }

  async tick() {
    if (!this.shop?.shop_id) return;
    try {
      const res = await api.get(`/scan-to-print/shop/${encodeURIComponent(this.shop.shop_id)}/jobs`);
      const jobs = res.data?.jobs || [];
      const offlineJobs = jobs.filter((j) => j.status === "printer_offline" || j.status === "pending");
      for (const job of offlineJobs) {
        if (this.isElectron) {
          await this.executeAutoPrint(job);
        }
      }
    } catch (e) {
      console.warn("AutoPrint tick error:", e);
    }
  }

  /**
   * Start polling for pending print jobs
   */
  startAutoWatcher(shop, onNewJob) {
    this.shop = shop;
    if (this.isPolling) return;
    this.isPolling = true;

    console.log("[PrintQ AutoPrint] Watcher started for shop:", shop?.shop_id);

    this.pollInterval = setInterval(async () => {
      if (!this.shop?.shop_id) return;

      try {
        const res = await api.get(`/scan-to-print/shop/${encodeURIComponent(this.shop.shop_id)}/jobs`);
        const jobs = res.data?.jobs || [];

        // Find pending jobs
        const pendingJobs = jobs.filter((j) => j.status === "pending");

        for (const job of pendingJobs) {
          if (this.activePrintingJobs.has(job.job_id)) continue;

          if (this.shop?.auto_print_enabled && this.isElectron) {
            console.log(`[PrintQ AutoPrint] Auto-dispatching Job #${job.job_id}...`);
            await this.executeAutoPrint(job);
          } else {
            // Notify counter UI
            if (onNewJob) onNewJob(job);
          }
        }
      } catch (err) {
        // Suppress background poll errors
      }
    }, 4000);
  }

  stopAutoWatcher() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
    this.isPolling = false;
  }

  /**
   * Check if the configured printer for this job type is online
   */
  getPrinterForJob(job) {
    if (!this.shop?.machines || !Array.isArray(this.shop.machines)) return null;

    const isColor = job.print_type === "color";
    const targetType = isColor ? "color" : "bw";

    // 1. Find configured machine in shop setup
    const machine = this.shop.machines.find(
      (m) => m.machine_type === targetType && m.enabled !== false
    );

    if (!machine) return null;

    const configuredName = (machine.device_name || machine.machine_name || "").trim().toLowerCase();
    if (!configuredName) return null;

    // 2. Cross-reference with live Windows installed printers
    const liveMatch = this.detectedPrinters.find((p) => {
      const pName = (p.name || p.displayName || "").trim().toLowerCase();
      return pName === configuredName;
    });

    const isOffline = liveMatch ? liveMatch.status === "offline" || liveMatch.isOffline : false;

    return {
      machine,
      deviceName: machine.device_name || machine.machine_name,
      isOnline: !isOffline,
    };
  }

  /**
   * Automatically process and print a pending job in Electron
   */
  async executeAutoPrint(job) {
    if (this.activePrintingJobs.has(job.job_id)) return;

    const isColor = job.print_type === "color";
    const printerInfo = this.getPrinterForJob(job);
    const configuredDeviceName = printerInfo?.deviceName || "";

    // If no printer configured or printer is offline -> Move to Offline Queue
    if (!printerInfo || !printerInfo.isOnline) {
      console.warn(`[PrintQ AutoPrint] Printer is Offline/Not Configured for Job #${job.job_id}. Moving to Offline Queue.`);
      const form = new FormData();
      form.append("status", "printer_offline");
      try {
        await api.put(`/scan-to-print/jobs/${job.job_id}/status`, form);
        this.notify({
          type: "printer_offline",
          job,
          printerName: configuredDeviceName,
          message: `Printer "${configuredDeviceName || "Default"}" is Offline. Job #${job.job_id} moved to Offline Queue.`,
        });
      } catch (e) {
        console.warn("Status update notice:", e);
      }
      return;
    }

    // Printer is Online & Ready! Proceed to Direct Silent Print
    this.activePrintingJobs.add(job.job_id);

    try {
      // 1. Mark status as 'printing'
      const startForm = new FormData();
      startForm.append("status", "printing");
      await api.put(`/scan-to-print/jobs/${job.job_id}/status`, startForm);

      this.notify({
        type: "printing_started",
        job,
        printerName: configuredDeviceName,
        message: `Auto-printing Job #${job.job_id} to ${configuredDeviceName}...`,
      });

      // 2. Download original print file
      const downloadRes = await api.get(`/scan-to-print/jobs/${job.job_id}/download`, {
        responseType: "blob",
      });

      const blob = downloadRes.data;
      const isPdf = Boolean(
        job.original_filename?.toLowerCase().endsWith(".pdf") ||
        job.content_type?.includes("pdf")
      );

      let finalBlob = blob;
      if (isPdf && (!blob.type || !blob.type.includes("pdf"))) {
        finalBlob = new Blob([blob], { type: "application/pdf" });
      }

      // Verify PDF header if PDF
      if (isPdf) {
        const headerBuf = await finalBlob.slice(0, 5).arrayBuffer();
        const headerStr = new TextDecoder().decode(headerBuf);
        if (!headerStr.startsWith("%PDF")) {
          throw new Error("Invalid PDF data received from server.");
        }
      }

      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(finalBlob);
      });

      // 3. Dispatch to Windows Print Spooler via Electron Bridge
      const printResult = await window.jsmDesktop.printers.printDocument({
        dataUrl,
        printerName: configuredDeviceName,
        copies: Math.max(1, job.copies || 1),
        color: isColor,
        isPdf,
        fileName: job.original_filename || "document.pdf",
      });

      if (printResult?.success) {
        // 4. Mark status as 'printed'
        const doneForm = new FormData();
        doneForm.append("status", "printed");
        await api.put(`/scan-to-print/jobs/${job.job_id}/status`, doneForm);

        this.notify({
          type: "printed_success",
          job,
          printerName: configuredDeviceName,
          message: `Job #${job.job_id} (${job.original_filename || "Document"}) printed successfully! ✓`,
        });
      } else {
        throw new Error(printResult?.error || "Windows Print Spooler rejected the document.");
      }
    } catch (err) {
      console.error("Auto print execution error:", err);
      try {
        const failForm = new FormData();
        failForm.append("status", "failed");
        await api.put(`/scan-to-print/jobs/${job.job_id}/status`, failForm);
      } catch (e) {
        // Ignore
      }

      this.notify({
        type: "print_failed",
        job,
        printerName: configuredDeviceName,
        error: err.message,
        message: `Auto-print failed for Job #${job.job_id}: ${err.message}`,
      });
    } finally {
      this.activePrintingJobs.delete(job.job_id);
    }
  }

  /**
   * Manual Single Job Print (Silent in Electron / Clean PDF View in Web)
   */
  async printJobManually(job, shop) {
    if (!job) throw new Error("No job provided");

    const isColor = job.print_type === "color";
    const targetType = isColor ? "color" : "bw";
    const machine = shop?.machines?.find((m) => m.machine_type === targetType && m.enabled !== false);
    const targetPrinter = (machine?.device_name || machine?.machine_name || "").trim();

    // 1. Download file blob
    const res = await api.get(`/scan-to-print/jobs/${job.job_id}/download`, {
      responseType: "blob",
    });

    const blob = res.data;
    const isPdf = Boolean(
      job.original_filename?.toLowerCase().endsWith(".pdf") ||
      job.content_type?.includes("pdf")
    );

    let finalBlob = blob;
    if (isPdf && (!blob.type || !blob.type.includes("pdf"))) {
      finalBlob = new Blob([blob], { type: "application/pdf" });
    }

    // Verify PDF header if PDF
    if (isPdf) {
      const headerBuf = await finalBlob.slice(0, 5).arrayBuffer();
      const headerStr = new TextDecoder().decode(headerBuf);
      if (!headerStr.startsWith("%PDF")) {
        throw new Error("Invalid PDF data received from server.");
      }
    }

    // 2. Dispatch
    if (window.jsmDesktop?.printers?.printDocument) {
      // Electron Desktop Mode
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(finalBlob);
      });

      const result = await window.jsmDesktop.printers.printDocument({
        dataUrl,
        printerName: targetPrinter,
        copies: Math.max(1, job.copies || 1),
        color: isColor,
        isPdf,
        fileName: job.original_filename || "document.pdf",
      });

      if (result?.success) {
        const form = new FormData();
        form.append("status", "printed");
        await api.put(`/scan-to-print/jobs/${job.job_id}/status`, form);
        return { success: true, mode: "electron" };
      } else {
        throw new Error(result?.error || "Spooler error");
      }
    } else {
      // Web Browser Fallback Mode (NO about:blank)
      if (isPdf) {
        // Open the native PDF viewer directly with blobUrl
        const blobUrl = window.URL.createObjectURL(finalBlob);
        const printWin = window.open(blobUrl, "_blank");
        if (!printWin) {
          throw new Error("Popup blocked by browser. Please allow popups to view and print documents.");
        }
        printWin.focus();

        // Keep blob URL alive for 60 seconds
        setTimeout(() => {
          try {
            window.URL.revokeObjectURL(blobUrl);
          } catch (e) {}
        }, 60000);

        const form = new FormData();
        form.append("status", "printed");
        await api.put(`/scan-to-print/jobs/${job.job_id}/status`, form);
        return { success: true, mode: "browser" };
      } else {
        // Image HTML print fallback
        const dataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(finalBlob);
        });

        const printWindow = window.open("", "_blank");
        if (printWindow) {
          printWindow.document.write(`
            <!DOCTYPE html>
            <html>
              <head>
                <title>PrintQ — ${job.job_id}</title>
                <style>
                  @page { size: A4 portrait; margin: 0; }
                  body { margin: 0; padding: 0; background: #fff; display: flex; justify-content: center; align-items: center; }
                  img { width: 100vw; height: 100vh; object-fit: contain; }
                </style>
              </head>
              <body>
                <img src="${dataUrl}" onload="window.print(); window.close();" />
              </body>
            </html>
          `);
          printWindow.document.close();
        }
        const form = new FormData();
        form.append("status", "printed");
        await api.put(`/scan-to-print/jobs/${job.job_id}/status`, form);
        return { success: true, mode: "browser" };
      }
    }
  }
}

export const autoPrintService = new AutoPrintService();
